import axiosInstance from "../../axios";

const state = {
    showPaymentModal: false,
    subscription: null,
    paymentMethods: [],
    paymentHistory: [],
    loading: false,
    error: null,
    payrexxPaymentPage: null
};

const mutations = {
    SET_SHOW_PAYMENT_MODAL(state, show) {
        state.showPaymentModal = show;
    },
    SET_SUBSCRIPTION(state, subscription) {
        state.subscription = subscription;
    },
    SET_PAYMENT_METHODS(state, methods) {
        state.paymentMethods = methods;
    },
    SET_PAYMENT_HISTORY(state, history) {
        state.paymentHistory = history;
    },
    SET_LOADING(state, loading) {
        state.loading = loading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_PAYREXX_PAYMENT_PAGE(state, pageData) {
        state.payrexxPaymentPage = pageData;
    },
    CLEAR_PAYREXX_PAYMENT_PAGE(state) {
        state.payrexxPaymentPage = null;
    }
};

const getters = {
    showPaymentModal: state => state.showPaymentModal,
    subscription: state => state.subscription,
    paymentMethods: state => state.paymentMethods,
    paymentHistory: state => state.paymentHistory,
    loading: state => state.loading,
    error: state => state.error,
    payrexxPaymentPage: state => state.payrexxPaymentPage,
    hasActiveSubscription: state => state.subscription && state.subscription.status === 'active',
    subscriptionPlan: state => state.subscription?.plan || { name: 'Free', price: 0 },
    nextBillingDate: state => state.subscription?.next_billing_at || state.subscription?.valid_until
};

const actions = {
    async fetchSubscription({ commit }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        try {
            const response = await axiosInstance({ requiresAuth: true }).get('/customer/subscription');
            const subscriptionData = response.data.data?.subscription;
            
            if (subscriptionData) {
                // Map backend fields to frontend expected structure
                const mappedSubscription = {
                    id: subscriptionData.id,
                    plan: {
                        name: subscriptionData.name,
                        price: parseFloat(subscriptionData.price),
                        currency: subscriptionData.currency
                    },
                    status: subscriptionData.status,
                    valid_until: subscriptionData.ends_at,
                    billing_cycle: subscriptionData.billing_period,
                    starts_at: subscriptionData.starts_at,
                    ends_at: subscriptionData.ends_at,
                    next_billing_at: subscriptionData.next_billing_at,
                    cancelled_at: subscriptionData.cancelled_at,
                    trial_ends_at: subscriptionData.trial_ends_at,
                    has_trial: subscriptionData.has_trial,
                    trial_days: subscriptionData.trial_days,
                    features: subscriptionData.features,
                    metadata: subscriptionData.metadata,
                    is_active: subscriptionData.is_active,
                    is_in_trial: subscriptionData.is_in_trial,
                    is_cancelled: subscriptionData.is_cancelled,
                    is_cancelled_with_access: subscriptionData.is_cancelled_with_access,
                    has_expired: subscriptionData.has_expired,
                    remaining_days: subscriptionData.remaining_days,
                    needs_payment: subscriptionData.needs_payment
                };
                commit('SET_SUBSCRIPTION', mappedSubscription);
            } else {
                // No subscription data found
                commit('SET_SUBSCRIPTION', {
                    plan: { name: 'Free', price: 0, currency: 'CHF' },
                    status: 'inactive',
                    valid_until: null,
                    billing_cycle: 'monthly'
                });
            }
        } catch (error) {
            if (error.response?.status === 404) {
                commit('SET_SUBSCRIPTION', {
                    plan: { name: 'Free', price: 0, currency: 'CHF' },
                    status: 'inactive',
                    valid_until: null,
                    billing_cycle: 'monthly'
                });
            } else {
                commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch subscription');
            }
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async fetchPaymentMethods({ commit }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        try {
            const response = await axiosInstance({ requiresAuth: true }).get('/customer/payments');
            commit('SET_PAYMENT_METHODS', response.data);
        } catch (error) {
            if (error.response?.status === 404) {
                commit('SET_PAYMENT_METHODS', []);
            } else {
                commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch payment methods');
            }
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async fetchPaymentHistory({ commit }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        try {
            const response = await axiosInstance({ requiresAuth: true }).get('/customer/payments');
            const paymentData = response.data.data || response.data;
            
            if (paymentData?.payments) {
                // Map backend payment data to frontend expected format
                const mappedPayments = paymentData.payments.map(payment => ({
                    id: payment.id,
                    date: payment.created_at,
                    description: payment.description,
                    amount: parseFloat(payment.amount),
                    currency: payment.currency,
                    status: payment.status,
                    invoice_url: payment.payment_page_url, // Use payment page URL as invoice URL for now
                    payrexx_transaction_id: payment.payrexx_transaction_id,
                    paid_at: payment.paid_at,
                    subscription: payment.subscription
                }));
                commit('SET_PAYMENT_HISTORY', mappedPayments);
            } else {
                commit('SET_PAYMENT_HISTORY', []);
            }
        } catch (error) {
            if (error.response?.status === 404) {
                commit('SET_PAYMENT_HISTORY', []);
            } else {
                commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch payment history');
            }
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async createPayrexxPaymentPage({ commit }, { planId, planType = 'monthly' }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        try {
            // Create subscription with payment page
            const subscriptionResponse = await axiosInstance({ requiresAuth: true }).post('/customer/subscriptions', {
                plan_id: planId,
                billing_cycle: planType,
                successRedirectUrl: `${window.location.origin}/payment-success.html`,
                failedRedirectUrl: `${window.location.origin}/payment-failed.html`,
                cancelRedirectUrl: `${window.location.origin}/payment-failed.html`
            });

            const responseData = subscriptionResponse.data.data || subscriptionResponse.data;
            
            // Check if we have a payment_url directly in the response
            if (responseData.payment_url) {
                commit('SET_PAYREXX_PAYMENT_PAGE', responseData);
                return responseData;
            }
            
            // Check if subscription data contains payment_url
            if (responseData.subscription?.payment_url) {
                const paymentData = {
                    payment_url: responseData.subscription.payment_url,
                    subscription: responseData.subscription
                };
                commit('SET_PAYREXX_PAYMENT_PAGE', paymentData);
                return paymentData;
            }
            
            // If subscription is returned, store it and return for further handling
            if (responseData.subscription) {
                commit('SET_SUBSCRIPTION', responseData.subscription);
                commit('SET_PAYREXX_PAYMENT_PAGE', responseData);
                return responseData;
            }
            
            // Fallback: return whatever we got
            commit('SET_PAYREXX_PAYMENT_PAGE', responseData);
            return responseData;
        } catch (error) {
            if (error.response?.status === 404) {
                commit('SET_ERROR', 'Payment integration is in development. Backend endpoints are not yet implemented.');
                return null;
            } else {
                commit('SET_ERROR', error.response?.data?.message || 'Failed to create subscription');
                return null;
            }
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async updateSubscription({ commit }, { planId, planType = 'monthly' }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        try {
            const response = await axiosInstance({ requiresAuth: true }).post('/customer/subscriptions', {
                plan_id: planId,
                billing_cycle: planType,
                successRedirectUrl: `${window.location.origin}/payment-success.html`,
                failedRedirectUrl: `${window.location.origin}/payment-failed.html`,
                cancelRedirectUrl: `${window.location.origin}/payment-failed.html`
            });
            commit('SET_PAYREXX_PAYMENT_PAGE', response.data);
            return response.data;
        } catch (error) {
            commit('SET_ERROR', error.response?.data?.message || 'Failed to update subscription');
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async cancelSubscription({ commit }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        try {
            const response = await axiosInstance({ requiresAuth: true }).delete('/customer/subscription', {
                data: {
                    successRedirectUrl: `${window.location.origin}/payment-success.html?action=cancelled`,
                    failedRedirectUrl: `${window.location.origin}/payment-failed.html?action=cancel_failed`,
                    cancelRedirectUrl: `${window.location.origin}/payment-failed.html?action=cancel_failed`
                }
            });
            
            const responseData = response.data.data || response.data;
            
            // Map backend response to frontend expected structure
            if (responseData.subscription) {
                const mappedSubscription = {
                    id: responseData.subscription.id,
                    plan: {
                        name: responseData.subscription.name,
                        price: parseFloat(responseData.subscription.price),
                        currency: responseData.subscription.currency
                    },
                    status: responseData.subscription.status,
                    valid_until: responseData.subscription.ends_at,
                    billing_cycle: responseData.subscription.billing_period,
                    starts_at: responseData.subscription.starts_at,
                    ends_at: responseData.subscription.ends_at,
                    next_billing_at: responseData.subscription.next_billing_at,
                    cancelled_at: responseData.subscription.cancelled_at,
                    trial_ends_at: responseData.subscription.trial_ends_at,
                    has_trial: responseData.subscription.has_trial,
                    trial_days: responseData.subscription.trial_days,
                    features: responseData.subscription.features,
                    metadata: responseData.subscription.metadata,
                    is_active: responseData.subscription.is_active,
                    is_in_trial: responseData.subscription.is_in_trial,
                    is_cancelled: responseData.subscription.is_cancelled,
                    is_cancelled_with_access: responseData.subscription.is_cancelled_with_access,
                    has_expired: responseData.subscription.has_expired,
                    remaining_days: responseData.subscription.remaining_days,
                    needs_payment: responseData.subscription.needs_payment
                };
                commit('SET_SUBSCRIPTION', mappedSubscription);
            }
            
            return responseData;
        } catch (error) {
            commit('SET_ERROR', error.response?.data?.message || 'Failed to cancel subscription');
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async reactivateSubscription({ commit }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        try {
            const response = await axiosInstance({ requiresAuth: true }).post('/customer/subscription/reactivate');
            const responseData = response.data.data || response.data;
            
            // Map backend response to frontend expected structure
            if (responseData.subscription) {
                const mappedSubscription = {
                    id: responseData.subscription.id,
                    plan: {
                        name: responseData.subscription.name,
                        price: parseFloat(responseData.subscription.price),
                        currency: responseData.subscription.currency
                    },
                    status: responseData.subscription.status,
                    valid_until: responseData.subscription.ends_at,
                    billing_cycle: responseData.subscription.billing_period,
                    starts_at: responseData.subscription.starts_at,
                    ends_at: responseData.subscription.ends_at,
                    next_billing_at: responseData.subscription.next_billing_at,
                    trial_ends_at: responseData.subscription.trial_ends_at,
                    features: responseData.subscription.features,
                    is_active: responseData.subscription.is_active,
                    is_in_trial: responseData.subscription.is_in_trial,
                    is_cancelled: responseData.subscription.is_cancelled,
                    cancelled_at: responseData.subscription.cancelled_at,
                    needs_payment: responseData.subscription.needs_payment
                };
                commit('SET_SUBSCRIPTION', mappedSubscription);
            }
            return responseData;
        } catch (error) {
            commit('SET_ERROR', error.response?.data?.message || 'Failed to reactivate subscription');
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async addPaymentMethod({ commit, state }, { subscriptionId }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        try {
            const subId = subscriptionId || state.subscription?.id;
            if (!subId) {
                throw new Error('No subscription found to add payment method');
            }
            
            const response = await axiosInstance({ requiresAuth: true }).post(`/customer/subscriptions/${subId}/pay`, {
                successRedirectUrl: `${window.location.origin}/payment-success.html?transaction_id=${subId}`,
                failedRedirectUrl: `${window.location.origin}/payment-failed.html?transaction_id=${subId}`,
                cancelRedirectUrl: `${window.location.origin}/payment-failed.html?transaction_id=${subId}`
            });
            commit('SET_PAYREXX_PAYMENT_PAGE', response.data);
            return response.data;
        } catch (error) {
            if (error.response?.status === 404) {
                commit('SET_ERROR', 'Payment method integration is in development. Backend endpoints are not yet implemented.');
                return null;
            } else {
                commit('SET_ERROR', error.response?.data?.message || 'Failed to add payment method');
                return null;
            }
        } finally {
            commit('SET_LOADING', false);
        }
    },

    openPaymentModal({ commit }) {
        commit('SET_SHOW_PAYMENT_MODAL', true);
    },

    closePaymentModal({ commit }) {
        commit('SET_SHOW_PAYMENT_MODAL', false);
    },

    clearPayrexxPaymentPage({ commit }) {
        commit('CLEAR_PAYREXX_PAYMENT_PAGE');
    }
};

export default {
    namespaced: true,
    state,
    mutations,
    getters,
    actions
};
